import React, { useState, useEffect, useCallback } from 'react';
import { X, Music, MapPin, Calendar, CreditCard, Star, Minus, Plus, Loader2, Mail, User, ArrowLeft, Send } from 'lucide-react';
import { slugForEvent } from '../data/mockData';
import { formatEventDay } from '../utils/formatEventDay';
import EmbeddedPaymentStep from './EmbeddedPaymentStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseDrawer = ({ event, isOpen, onClose, userId, isLoggedIn, userName, userEmail, initialTicketId = null, initialQuantity = 1, skipTicketStep = false }) => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [quantity, setQuantity] = useState(initialQuantity || 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [step, setStep] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  // Embedded Payment Element state
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');

  const handleShare = async () => {
    if (!event) return;
    const slug = event.slug || slugForEvent(event.id);
    const link = slug
      ? `${window.location.origin}/api/e/${slug}`
      : `${window.location.origin}/api/share/event/${event.id}`;
    const shareData = { title: event.name, text: `Echa un vistazo a ${event.name} en Clubbers`, url: link };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (event) {
      setQuantity(initialQuantity || 1);
      setError('');
      setPaymentSuccess(false);
      // Si venimos con una entrada ya elegida desde el detalle, saltamos el paso 1.
      setStep(skipTicketStep ? 2 : 1);
      // Logged-in users: use account name/email and skip the contact form
      if (isLoggedIn) {
        setBuyerName(userName || '');
        setBuyerEmail(userEmail || '');
      } else {
        setBuyerName(localStorage.getItem('user_name') || '');
        setBuyerEmail('');
      }
      // Fetch tickets from backend (single source of truth)
      setTicketsLoading(true);
      (async () => {
        try {
          const res = await fetch(`${API}/ticket-packages`);
          const data = await res.json();
          const evTickets = data[String(event.id)] || [];
          setTickets(evTickets);
          // Respetar la entrada preseleccionada si viene en los props
          const preSel = initialTicketId != null
            ? evTickets.find((t) => Number(t.id) === Number(initialTicketId) && !t.soldOut)
            : null;
          const firstAvailable = evTickets.find((t) => !t.soldOut) || evTickets[0] || null;
          setSelectedTicket(preSel || firstAvailable);
        } catch (err) {
          setTickets([]);
          setError('No se pudieron cargar las entradas. Inténtalo de nuevo.');
        } finally {
          setTicketsLoading(false);
        }
      })();
    }
  }, [event, isLoggedIn, userName, userEmail, initialTicketId, initialQuantity, skipTicketStep]);

  // Si el usuario está logueado y venimos con entrada preseleccionada, saltamos directo al pago.
  useEffect(() => {
    if (!skipTicketStep || !isLoggedIn) return;
    if (step !== 2) return;
    if (!selectedTicket || !event) return;
    if (!buyerName.trim() || !buyerEmail.trim() || !buyerEmail.includes('@')) return;
    openEmbeddedPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipTicketStep, isLoggedIn, step, selectedTicket, event, buyerName, buyerEmail]);

  const ticketPrice = Number(selectedTicket?.price) || 0;
  const total = ticketPrice * quantity;
  const pointsEarned = Math.floor(total * 10);
  const isFormValid = buyerName.trim() && buyerEmail.trim() && buyerEmail.includes('@');

  // Create PaymentIntent on the backend then advance to step 3 (embedded Stripe Payment Element).
  const openEmbeddedPayment = useCallback(async () => {
    if (!selectedTicket || !event || !isFormValid) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId || '' },
        body: JSON.stringify({
          event_id: event.id,
          ticket_id: selectedTicket.id,
          quantity,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Error creando el pago'); }
      const data = await res.json();
      setClientSecret(data.client_secret);
      setPaymentIntentId(data.payment_intent_id);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Error al iniciar el pago');
    } finally {
      setLoading(false);
    }
  }, [event, selectedTicket, quantity, buyerName, buyerEmail, isFormValid, userId]);

  const handleContinue = () => {
    if (!selectedTicket) { setError('Selecciona una entrada'); return; }
    setError('');
    if (isLoggedIn) {
      // Logged-in: skip the contact form, open embedded payment using account data
      handlePay();
    } else {
      setStep(2);
    }
  };

  const handlePay = () => {
    if (!isFormValid) { setError('Por favor, introduce tu nombre y email'); return; }
    if (window.gtag && selectedTicket) {
      window.gtag('event', 'begin_checkout', {
        currency: 'EUR',
        value: total,
        items: [{ item_name: event?.name, item_id: String(event?.id), price: selectedTicket.price, quantity }],
      });
    }
    openEmbeddedPayment();
  };

  if (!isOpen || !event) return null;

  // Loading overlay shown while preparing the embedded payment (creating PaymentIntent).
  const loadingOverlay = loading ? (
    <div className="fixed inset-0 z-[200] bg-[#0a0a0a] flex flex-col items-center justify-center px-6 text-center" data-testid="purchase-loading-overlay">
      <Loader2 size={42} className="text-[#F82C47] animate-spin mb-5" />
      <p className="text-white font-bold text-lg">Preparando pago seguro</p>
      <p className="text-gray-400 text-sm mt-1">Un momento…</p>
    </div>
  ) : null;

  if (paymentSuccess) {
    return (
      <div className="fixed inset-0 z-[100] flex items-end justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className="relative w-full max-w-md glass-dark rounded-t-3xl animate-slide-up p-8 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-white text-xl font-bold mb-2">¡Pago exitoso!</h3>
          <p className="text-gray-400 mb-1">Tu entrada para {event.name} ha sido confirmada.</p>
          <p className="text-gray-400 text-sm mb-6">Se enviará un email de confirmación a <span className="text-[#F82C47]">{buyerEmail}</span></p>
          <button onClick={onClose} className="w-full py-3 rounded-full text-white font-bold" style={{ backgroundColor: '#F82C47' }}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    {loadingOverlay}
    <div className="fixed inset-0 z-[100] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md glass-dark rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2"><div className="w-10 h-1 bg-white/30 rounded-full" /></div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => { setStep(step - 1); setError(''); }} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="purchase-back-button">
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-white text-2xl font-bold">
              {step === 1 ? 'Comprar Entrada' : step === 2 ? 'Datos de contacto' : 'Método de pago'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {copied && (
              <span className="text-[#F82C47] text-xs font-semibold" data-testid="share-copied-msg">¡Copiado!</span>
            )}
            <button onClick={handleShare} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white hover:text-[#F82C47] transition-colors" data-testid="purchase-share-button" aria-label="Compartir evento">
              <Send size={16} />
            </button>
            <button onClick={onClose} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white" data-testid="purchase-close-button"><X size={18} /></button>
          </div>
        </div>
        {/* Event Image */}
        <div className="px-5 mb-4">
          <img src={event.image} alt={event.name} className="w-full h-48 object-cover rounded-2xl" />
        </div>

        {step === 1 && (
        <>
        {/* Tags */}
        <div className="px-5 flex gap-2 mb-3">
          <span className="glass-dark flex items-center gap-1 rounded-full px-3 py-1 text-white text-sm">
            <Calendar size={12} className="text-white" /> Evento
          </span>
          <span className="glass-dark flex items-center gap-1 rounded-full px-3 py-1 text-white text-sm">
            <Star size={12} className="text-white fill-white" /> +{pointsEarned} pts
          </span>
        </div>
        {/* Event Details */}
        <div className="px-5 mb-4">
          <h3 className="text-white text-2xl font-bold mb-2">{event.name}</h3>
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1"><Music size={14} /><span>{event.venue}</span></div>
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1"><MapPin size={14} /><span>{event.location}</span></div>
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3"><Calendar size={14} /><span>{formatEventDay(event.day)}</span></div>
          <p className="text-gray-400 text-sm">{event.description}</p>
        </div>
        {/* Tickets */}
        <div className="px-5 mb-4">
          <h4 className="text-white font-bold text-lg mb-3">Entradas</h4>
          <div className="space-y-3">
            {ticketsLoading && tickets.length === 0 && (
              <div className="flex items-center justify-center py-6 text-gray-400 text-sm gap-2">
                <Loader2 size={18} className="animate-spin" /> Cargando entradas...
              </div>
            )}
            {!ticketsLoading && tickets.length === 0 && (
              <div className="glass rounded-2xl p-5 text-center text-gray-300 text-sm" data-testid="tickets-empty-state">
                No hay entradas disponibles para este evento todavía.
              </div>
            )}
            {tickets.map((ticket) => {
              const isSelected = selectedTicket?.id === ticket.id;
              const sold = ticket.soldOut;
              return (
                <button key={ticket.id} disabled={sold} onClick={() => !sold && setSelectedTicket(ticket)}
                  data-testid={`ticket-option-${ticket.id}`}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all glass ${sold ? 'opacity-60 cursor-not-allowed' : isSelected ? 'ring-2 ring-[#F82C47]' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected && !sold ? 'border-[#F82C47]' : 'border-white/40'}`}>
                      {isSelected && !sold && <div className="w-2.5 h-2.5 rounded-full bg-[#F82C47]" />}
                    </div>
                    <div className="text-left">
                      <span className={`block font-bold text-sm ${sold ? 'text-gray-500 line-through' : isSelected ? 'text-[#F82C47]' : 'text-white'}`}>{ticket.label}</span>
                      {ticket.note && !sold && <span className="text-[#F82C47] text-xs font-medium">{ticket.note}</span>}
                      {ticket.details && isSelected && !sold && (
                        <ul className="mt-2 space-y-1">
                          {ticket.details.map((d, i) => (
                            <li key={i} className="text-gray-300 text-xs flex items-start gap-1.5">
                              <span className="text-[#F82C47] mt-0.5">•</span><span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {sold
                    ? <span className="text-gray-500 text-xs font-bold uppercase tracking-wide">Agotadas</span>
                    : <span className={`font-bold whitespace-nowrap no-underline ${isSelected ? 'text-[#F82C47]' : 'text-white'}`} style={{ textDecoration: 'none' }}>{`${Number(ticket.price || 0).toFixed(2)}€`}</span>}
                </button>
              );
            })}
          </div>
        </div>
        {/* Quantity */}
        <div className="px-5 mb-4">
          <div className="glass flex items-center justify-between rounded-2xl p-4">
            <span className="text-white font-bold">Cantidad</span>
            <div className="flex items-center gap-4">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white"><Minus size={16} /></button>
              <span className="text-white font-bold text-lg w-6 text-center">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="glass-dark w-9 h-9 rounded-full flex items-center justify-center text-white"><Plus size={16} /></button>
            </div>
          </div>
        </div>
        </>
        )}
        {/* Summary */}
        <div className="px-5 mb-4">
          <div className="glass rounded-2xl p-4">
            <div className="flex justify-between text-gray-400 text-sm mb-1">
              <span>Entrada {selectedTicket?.label || '—'}</span>
              <span key={`sub-${ticketPrice}`} className="whitespace-nowrap tabular-nums" translate="no" style={{ textDecoration: 'none', WebkitTextDecorationLine: 'none', textDecorationLine: 'none', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1, "zero" 0, "ss01" 0, "ss02" 0' }}>{`${ticketPrice.toFixed(2)}€`}</span>
            </div>
            <div className="flex justify-between text-gray-400 text-sm mb-3 pb-3 border-b border-white/10">
              <span>Cantidad</span><span>x{quantity}</span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-white font-bold">Total</span>
              <span key={`total-${total}`} className="text-[#F82C47] font-bold text-xl whitespace-nowrap tabular-nums" translate="no" style={{ textDecoration: 'none', WebkitTextDecorationLine: 'none', textDecorationLine: 'none', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1, "zero" 0, "ss01" 0, "ss02" 0' }}>{`${total.toFixed(2)}€`}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Puntos a ganar</span>
              <div className="glass-dark flex items-center gap-1 rounded-full px-2 py-0.5">
                <Star size={12} className="text-white fill-white" />
                <span className="text-white text-xs font-medium">+{pointsEarned}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Continue / Pay Button (Step 1) */}
        {step === 1 && (
          <div className="px-5 pb-8">
            <button onClick={handleContinue} disabled={!selectedTicket || loading}
              className="w-full py-4 rounded-full overflow-hidden text-white font-bold text-lg disabled:opacity-60"
              style={{ backgroundColor: '#F82C47' }} data-testid="purchase-continue-button">
              {isLoggedIn
                ? (loading
                  ? <span key="loading" className="flex items-center justify-center gap-2"><Loader2 size={20} className="animate-spin" /> Procesando...</span>
                  : <span key="ready" className="flex items-center justify-center gap-2"><CreditCard size={20} /> Pagar {total.toFixed(2)}€</span>)
                : <span key="continue" className="flex items-center justify-center">Continuar</span>}
            </button>
          </div>
        )}

        {step === 2 && (
        <>
        {/* Buyer Info */}
        <div className="px-5 mb-4">
          <h4 className="text-white font-bold text-lg mb-3">Datos de contacto</h4>
          <div className="space-y-3">
            <div className="glass relative rounded-full">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
              <input type="text" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Tu nombre"
                className="w-full bg-transparent border-none rounded-full pl-10 pr-4 py-3 text-white placeholder-white/50 outline-none" data-testid="buyer-name-input" />
            </div>
            <div className="glass relative rounded-full">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
              <input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} placeholder="tu@email.com"
                className="w-full bg-transparent border-none rounded-full pl-10 pr-4 py-3 text-white placeholder-white/50 outline-none" data-testid="buyer-email-input" />
            </div>
          </div>
        </div>

        {/* Error */}
        {error && <div className="px-5 mb-3"><p className="text-[#F82C47] text-sm text-center">{error}</p></div>}

        {/* Pay Button — opens embedded Stripe Payment Element on step 3 */}
        <div className="px-5 pb-8">
          <button onClick={handlePay} disabled={loading || !isFormValid}
            className="w-full py-4 rounded-full overflow-hidden text-white font-bold text-lg disabled:opacity-60"
            style={{ backgroundColor: '#F82C47' }} data-testid="purchase-pay-button">
            {loading
              ? <span key="loading" className="flex items-center justify-center gap-2"><Loader2 size={20} className="animate-spin" /> Procesando...</span>
              : <span key="ready" className="flex items-center justify-center gap-2"><CreditCard size={20} /> Pagar {total.toFixed(2)}€</span>}
          </button>
        </div>
        </>
        )}

        {/* Step 3 — Embedded Stripe Payment Element */}
        {step === 3 && (
          <div className="px-5 pb-8">
            <EmbeddedPaymentStep
              clientSecret={clientSecret}
              amount={total}
              eventName={event?.name}
              onError={(msg) => setError(msg)}
            />
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default PurchaseDrawer;
